<script setup lang="ts">
import type {
	ProductContentsResponseSchema,
	ProductSchema,
} from '@laam/cms-shared';
import { VideoType } from '@laam/ui/base';
import {
	PhPause,
	PhPlay,
	PhSpeakerSimpleHigh,
	PhSpeakerSimpleSlash,
} from '@phosphor-icons/vue';
import { toRefs, useThrottleFn } from '@vueuse/core';
import Hls from 'hls.js';
import { inject, onMounted, onUnmounted, ref, watch } from 'vue';
import {
	type VideosInteractionMetaData,
	type VideosMetaData,
} from '../../../../cathode/src/events/index.ts';
import { type VideoPauseEvent, type VideoPlayEvent } from './types';
import { useMute } from './utils.ts';

interface Props {
	videoUrl: string;
	src: string;
	isPlaying: boolean;
	poster?: string;
	videoType?: VideoType;
	productId?: number;
	isMuted: boolean;
}
const props = defineProps<Props>();
const { videoUrl, isMuted } = toRefs(props);
const emit = defineEmits<{
	(e: 'cathode:pauseEvent', payload: VideosMetaData): void;
	(e: 'play', payload: VideoPlayEvent): void;
	(e: 'interaction', payload: VideosInteractionMetaData): void;
	(e: 'pause', payload: VideoPauseEvent): void;
	(e: 'loadeddata'): void;
}>();

const { toggleMute } = useMute();

type VideoEventEmitter<T> = (event: { payload: T }) => void;

const emitVideoPlay = inject<VideoEventEmitter<VideoPlayEvent>>('video-play');
const emitVideoPause =
	inject<VideoEventEmitter<VideoPauseEvent>>('video-pause');
const emitCathodePause = inject<VideoEventEmitter<VideosMetaData>>(
	'cathode-video-pause',
);
const emitInteraction = inject<VideoEventEmitter<VideosInteractionMetaData>>(
	'cathode-video-interaction',
);

const video = ref<HTMLVideoElement | null>(null);
let hls: Hls | null = null;
const totalWatchTime = ref(0);
const lastTime = ref(0);
let bufferingTime: any = null;

const videoInteractions = [
	'waiting',
	'volumechange',
	'seeked',
	'fullscreenchange',
	'play',
	'pause',
] as const;

const videoEventHandlers = new Map();

const productData = inject<ProductSchema | undefined>('productData', undefined);
const contentData = inject(
	'contentData',
) as ProductContentsResponseSchema['contents'][number];
const isProductVideo = inject('isProductVideo') as boolean;
const storeID = inject('storeID') as number;
const carouselType = inject<string>('carousel-type');
const parentPage = inject<string>('parent');
const isVideoPlaying = ref(false);
const showControls = ref(true);
const seekPercentage = ref(0);
const isVisible = ref(false);
let hideTimer: ReturnType<typeof setTimeout> | null = null;

const onLoadedData = () => {
	emit('loadeddata');
};

const preparePlayer = () => {
	if (!video.value) return;
	if (props.videoType === VideoType.HLS) {
		if (hls) {
			hls.destroy();
		}
		if (Hls.isSupported()) {
			hls = new Hls();
			hls.loadSource(props.src);
			hls.attachMedia(video.value);
		} else {
			video.value.src = props.src;
		}
	} else {
		video.value.src = props.src;
	}
	video.value.load();
};

onMounted(() => {
	if (isProductVideo) {
		preparePlayer();
		if (video.value) {
			video.value?.play().catch(() => {});
		}
	}

	props.videoType === VideoType.HLS && preparePlayer();

	if (video.value) {
		if (props.isPlaying) {
			video.value?.play().catch(() => {});
		}
		const observer = new IntersectionObserver(([entry]) => {
			isVisible.value = entry ? entry.isIntersecting : false;
		});
		observer.observe(video.value);
		video.value.addEventListener('play', onPlay);
		video.value.addEventListener('pause', onEnded);
		video.value.addEventListener('timeupdate', onTimeUpdate);
		video.value.addEventListener('waiting', onWaiting);
		video.value.addEventListener('playing', onResumeFromBuffering);
		video.value.addEventListener('loadeddata', onLoadedData);

		videoInteractions.forEach((interaction) => {
			const handler = ['seeked', 'volumechange', 'fullscreenchange'].includes(
				interaction,
			)
				? (event: Event) => sendThrottledEvents(event.type)
				: (event: Event) => onVideoInteraction(event.type);
			video.value!.addEventListener(interaction, handler);
			videoEventHandlers.set(interaction, handler);
		});
	}
});
onUnmounted(() => {
	if (video.value) {
		videoEventHandlers.forEach((handler, interaction) => {
			video.value!.removeEventListener(interaction, handler);
		});
		video.value.removeEventListener('loadeddata', onLoadedData);
	}
	hls?.destroy();
});

const onPlay = () => {
	isVideoPlaying.value = true;
	lastTime.value = video.value?.currentTime || 0;

	if (video.value) {
		const playPayload: VideoPlayEvent = {
			productId:
				productData?.shopify_id || contentData?.product_shopify_id || 0,
			videoUrl: videoUrl.value,
			totalDuration: video.value.duration,
			stockStatus: productData?.stock_status || contentData?.stock_status,
			carouselType: carouselType,
			source: window.location.href,
		};
		emit('play', playPayload);

		emitVideoPlay?.({
			payload: playPayload,
		});
		resetControlsTimeout();
	}
};

const onTimeUpdate = () => {
	if (!video.value) return;
	const currentTime = video.value.currentTime;
	const delta = currentTime - lastTime.value;
	if (delta > 0) {
		totalWatchTime.value += delta;
		lastTime.value = currentTime;
	}
};

const onWaiting = () => {
	if (bufferingTime === null) {
		bufferingTime = performance.now();
	}
};
const onResumeFromBuffering = () => {
	if (bufferingTime !== null) {
		const waitingDuration = (
			(performance.now() - bufferingTime) /
			1000
		).toFixed(3);
		bufferingTime = null;
		onVideoInteraction('waiting', waitingDuration);
	}
};

const onEnded = () => {
	isVideoPlaying.value = false;
	if (video.value) {
		const pausePayload: VideoPauseEvent = {
			watchedTime: totalWatchTime.value.toFixed(3),
			totalDuration: video.value.duration,
			no_of_times_played: video.value.duration
				? Number((totalWatchTime.value / video.value.duration).toFixed(3))
				: 0,
			productId:
				productData?.shopify_id || contentData?.product_shopify_id || 0,
			videoUrl: videoUrl.value,
			stockStatus: productData?.stock_status || contentData?.stock_status,
			carouselType: carouselType,
			source: window.location.href,
		};

		const cathodePausePayload: VideosMetaData = {
			component: carouselType,
			source: window.location.href,
			page: parentPage,
			store_id: storeID,
			data: [
				{
					no_of_times: video.value.duration
						? Math.floor(totalWatchTime.value / video.value.duration)
						: 0,
					total_duration: video.value.duration.toFixed(3),
					watched_duration: totalWatchTime.value.toFixed(3),
					video_url: videoUrl.value,
					id: productData?.shopify_id || contentData?.product_shopify_id || 0,
					inventory:
						productData?.total_inventory_quantity ||
						contentData?.total_inventory_quantity,
					content_id:
						productData?.content_videos?.[0]?.content_id ??
						contentData?.content_id ??
						0,
					placement_source: contentData?.placement_source
						? contentData.placement_source
						: productData?.placement_source,
				},
			],
		};

		emitVideoPause?.({
			payload: pausePayload,
		});

		emitCathodePause?.({
			payload: cathodePausePayload,
		});
	}
};

const resetControlsTimeout = () => {
	showControls.value = true;
	if (hideTimer) clearTimeout(hideTimer);
	if (isVideoPlaying.value) {
		hideTimer = setTimeout(() => {
			showControls.value = false;
		}, 2000);
	}
};

const togglePlay = async () => {
	try {
		if (video?.value?.paused) {
			await video.value.play();
		} else {
			video?.value?.pause();
		}
		resetControlsTimeout();
	} catch (e) {
		console.error('Playback error:', e);
	}
};

const updateSeekBar = () => {
	if (!video.value) return;
	seekPercentage.value = (video.value.currentTime / video.value.duration) * 100;
};

const seekVideo = (e: any) => {
	if (!video.value) return;

	const rect = e.currentTarget.getBoundingClientRect();
	const seeked = e.clientX - rect.left;
	const totalWidth = rect.width;

	const percentage = seeked / totalWidth;
	video.value.currentTime = video.value.duration * percentage;
};

const onVideoInteraction = (
	interactionType: string,
	bufferingTime?: string,
) => {
	if (video.value) {
		const interactionPayload: VideosInteractionMetaData = {
			component: carouselType,
			source: window.location.href,
			data_source: '',
			page: parentPage,
			store_id: storeID,
			data: [
				{
					video_url: videoUrl.value ?? '',
					product_id:
						(productData?.shopify_id || contentData?.product_shopify_id) ?? 0,
					interaction_type: interactionType,
					waiting_time_period: bufferingTime ?? '0',
					content_id:
						productData?.content_videos?.[0]?.content_id ??
						contentData?.content_id ??
						0,
					placement_source: contentData?.placement_source
						? contentData.placement_source
						: productData?.placement_source,
				},
			],
		};

		emitInteraction?.({
			payload: interactionPayload,
		});
	}
};

const sendThrottledEvents = useThrottleFn(onVideoInteraction, 1000);
watch([() => props.isPlaying, isVisible], ([isPlaying, visible]) => {
	if (isPlaying && visible) {
		video.value?.play().catch(() => {});
	} else {
		video.value?.pause();
	}
	resetControlsTimeout();
});
watch(
	() => props.src,
	() => {
		preparePlayer();
	},
);
</script>

<template>
	<div class="w-full h-full flex-col justify-end">
		<video
			ref="video"
			class="size-full object-cover"
			:autoplay="true"
			disablepictureinpicture
			loop
			:muted="!props.isPlaying || isMuted"
			playsinline
			webkit-playsinline
			webkit-disablepictureinpicture
			:poster
			preload="metadata"
			@click="togglePlay"
			@timeupdate="updateSeekBar"
		></video>
		<button
			v-if="showControls"
			class="absolute z-10 bg-black/30 backdrop-blur-md p-2 rounded-full w-[72px] h-[72px] flex justify-center items-center transition-opacity ease-linear duration-500"
			:class="{ 'opacity-0': !showControls, 'opacity-100': showControls }"
			@click.stop="togglePlay"
		>
			<component
				:is="isVideoPlaying ? PhPause : PhPlay"
				:size="28"
				:weight="'fill'"
				:color="'#ffffff'"
			/>
		</button>

		<button
			class="absolute right-5xl top-8xl z-10 bg-[#141414] backdrop-blur-md opacity-70 p-2 rounded-full w-9xl h-9xl flex justify-center items-center transition-opacity duration-500"
			@click.stop="toggleMute"
		>
			<component
				:is="isMuted ? PhSpeakerSimpleSlash : PhSpeakerSimpleHigh"
				:size="20"
				:color="'#ffffff'"
			/>
		</button>
		<div class="absolute bottom-0 h-xs w-full">
			<div
				ref="seekBar"
				class="w-full h-xs absolute cursor-pointer bottom-0"
				@click="seekVideo"
			>
				<div
					class="h-xs bg-white transition-all duration-200 ease-linear"
					:style="{ width: seekPercentage + '%' }"
				></div>
			</div>
		</div>
	</div>
</template>

<style scoped>
video::-webkit-media-controls-picture-in-picture-button {
	display: none !important;
}
video::-webkit-media-fullscreen-button {
	display: none !important;
}
</style>
